import moment from "moment"
import { PropsWithChildren, useContext, useEffect, useState } from "react"
import { useFormContext, useWatch } from "react-hook-form"
import { PrintTransactionModal, TableHeader } from "components"
import { convertNumber, useCountryById, useHandleAuth, useProvinceById, useRegencyById, useSubdistrictById } from "utils"
import { useSubdomainSetting } from "pages/Purchase/Transaction/PurchaseOrder/utils"
import { useSupplier } from "pages/Purchase/Transaction/components/Supplier/utils"
import { useList } from "pages/Setup/Preference/SettingCompany/utils/hooks"
import { FormType } from "../utils"
import { JournalList } from "./JournalList"
import { AccountContext } from "contexts"
import { BillContext } from "../contexts"

export function PrintPaymentBill(params: { toggle: () => void }): JSX.Element {
  const { control } = useFormContext<FormType>()
  const transaction_date = useWatch({ control, name: "transaction_date" })
  const trx_code = useWatch({ control, name: "trx_code" })
  const coa_id = useWatch({ control, name: "coa_id" })
  const _balance = useWatch({ control, name: "_balance" })
  const referensi = useWatch({ control, name: "referensi" })
  const memo = useWatch({ control, name: "memo" })
  const deposit_amount = useWatch({ control, name: "deposit_amount" })
  const payment_amount = useWatch({ control, name: "payment_amount" })

  const dataAccount = useContext(AccountContext)
  const dataBill = useContext(BillContext)

  const setting = useSubdomainSetting()
  const company = useList()
  const country = useCountryById()
  const province = useProvinceById()
  const regency = useRegencyById()
  const subdistrict = useSubdistrictById()
  const supplier = useSupplier()
  
  const { userTokenData } = useHandleAuth()
  const userToken = userTokenData()

  const [address, setAddress] = useState<string>("")

  const value: number = dataBill?.databill?.balance - deposit_amount - payment_amount

  useEffect(() => {
    company.fetchData().then((res) => {
      setAddress(res.address)

      if (res.subdistrict_id) {
        subdistrict.fetchData(res.subdistrict_id)
      }

      if (res.regency_id) {
        regency.fetchData(res.regency_id)
      }

      if (res.province_id) {
        province.fetchData(res.province_id)
      }

      if (res.country_id) {
        country.fetchData(res.country_id)
      }
    })

    // eslint-disable-next-line
  }, [])

  const getAddress = (): string => {
    let generateAddress = address ?? ""

    if (subdistrict.data) {
      generateAddress += ", " + subdistrict.data.subdistrict_name
    }

    if (regency.data) {
      generateAddress += ", " + regency.data.regency_name
    }

    if (province.data) {
      generateAddress += ", " + province.data.province_name
    }

    if (country.data) {
      generateAddress += ", " + country.data.country_name
    }

    return generateAddress
  }

  return (
    <PrintTransactionModal
      isLandscape
      isLoading={company.isLoading || supplier.isLoading || country.isLoading || province.isLoading || subdistrict.isLoading || regency.isLoading}
      toggle={params.toggle}
    >
      <section className="flex flex-col gap-3 text-[14px]">
        <div className="text-center text-2xl font-bold uppercase">
          {userToken?.company_name}
        </div>

        <section className="grid grid-cols-2 items-stretch gap-1">
          <section className="font-bold">
            <table className="table-top">
              <tbody className="font-bold">
                <InfoItems width={250} uppercase label="ADDRESS" text={getAddress()} />
                <InfoItems width={250} label="PHONE" text={setting.data?.phone!} />
                <InfoItems width={250} label="EMAIL" text={setting.data?.email!} />
                <InfoItems width={250} label="WEBSITE" text={setting.data?.website!} />
              </tbody>
            </table>
          </section>

          <section className="flex flex-col justify-between items-end">
            <div className="text-2xl font-bold text-right">
              PAYMENT BILL
            </div>

            <table>
              <tbody className="font-bold ">
                <InfoItems width={280} textWidth={200} textRight label="DATE" text={moment(transaction_date).format("DD/MM/YYYY")} />
                <InfoItems width={280} textWidth={200} textRight label="PAYMENT BILL NO" text={trx_code!} />
              </tbody>
            </table>
          </section>
        </section>

        <hr />

        <section className="grid grid-cols-2 items-stretch gap-1">
          <section className="font-bold">
            <table className="table-top">
              <tbody className="font-bold">
                <InfoItems label="PAYMENT FROM ACCOUNT" width={250} text={dataAccount?.data?.find((item) => item?.value === coa_id)?.label!} />
                <InfoItems uppercase label="PAY TO" width={250} text={dataBill?.databill?.vendor_name} />
                <InfoItems uppercase label="MEMO" width={250} text={memo} />
                <InfoItems uppercase label="BRANCH LOCATION" width={250} text={dataBill?.databill?.address} />
                <InfoItems uppercase label="LINK TO DELIVERY BILL ID" width={250} text={dataBill?.databill?.trx_code} />
              </tbody>
            </table>
          </section>

          <section className="flex flex-col justify-between items-end">
            <table className="table-top">
              <tbody className="font-bold ">
                <InfoItems width={280} textWidth={200} textRight label="BALANCE" text={_balance || "0"} />
                <InfoItems width={280} textWidth={200} textRight label="CC || DEBIT NO || CHEQUE NO" text={referensi || "0"} />
                <InfoItems width={280} textWidth={200} textRight label="BALANCE BILL" text={dataBill?.databill?.balance ? convertNumber(dataBill?.databill?.balance).intoCurrency : "0"} />
                <InfoItems width={280} textWidth={200} textRight label="PAYMENT FROM DEPOSIT" text={deposit_amount ? convertNumber(deposit_amount).intoCurrency : "0"} />
                <InfoItems width={280} textWidth={200} textRight label="PAYMENT AMOUNT" text={payment_amount ?  convertNumber(payment_amount).intoCurrency : "0"} />
                <InfoItems width={280} textWidth={200} textRight label="BALANCE" text={value ?  convertNumber(value).intoCurrency : "0"} />
              </tbody>
            </table>
          </section>
        </section>

        <table className="table table-bordered">
          <TableHeader>
            <tr>
              <th>DATE</th>
              <th>ID#</th>
              <th>ACCOUNT</th>
              <th>MEMO</th>
              <th>DEBET</th>
              <th>CREDIT</th>
            </tr>
          </TableHeader>
          <tbody>
            <JournalList.PaymentAccount />
            <JournalList.PaymentDeposit />
            <JournalList.Total />
          </tbody>
        </table>
      </section>
    </PrintTransactionModal>
  )
}

type InfoItemsType = {
  label: string
  text: string | number
  uppercase?: boolean
  textRight?: boolean
  width?: number
  textWidth?: number
}
const InfoItems = (props: PropsWithChildren<InfoItemsType>) => (
  <tr>
    <td width={props?.width ? props?.width : 120} className="pr-2 uppercase flex justify-between">
      <div>{props?.label}</div>
      <div>:</div>
    </td>
    <td width={props?.textWidth} className={props?.uppercase ? `uppercase` : props?.textRight ? "text-right" : ""}>{props?.text || "-"}</td>
  </tr>
)